import { Form, Modal } from "antd"
import { LoadingRegion } from "components/custom/Loader"
import React, { useEffect, useState } from "react"
import {
  IAddUserRequest,
  ICustomRoleRequest,
  IGetAdminsRes,
  IRole,
} from "../interface/interface"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useAppNotificationStore } from "stores"
import { Button } from "components"
import { AppPeoplePicker } from "components/inputs/select/AppPeoplePicker"
import usePermissions from "hooks/usePermissions"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { onQueryError } from "utils/utils"
import { COMPANY_ENDPOINTS, companyService } from "services/companyService"
import { roleService } from "services/roleService"

type Props = {
  role: IRole
  onClose: Function
  isCustomRole?: boolean
}

export const AssignMembersModal = ({
  role,
  onClose,
  isCustomRole = false,
}: Props) => {
  const { toast } = useAppNotificationStore()

  const [form] = Form.useForm()

  const [employees, setEmployees] = useState<string[]>([])

  const { data, isLoading: isGetting } = useQuery<IGetAdminsRes>({
    queryKey: [COMPANY_ENDPOINTS.GET_ADMINS, isCustomRole],
    queryFn: async () => companyService.getAdmins(isCustomRole),
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
  })

  const admins = data?.data ?? []

  const { mutate, isLoading } = useMutation({
    mutationFn: async (data: IAddUserRequest) => roleService.updateUsers(data),
    onSuccess: (data) => {
      data?.message && toast.success(data.message)
      onClose(true)
    },
    onError: onQueryError,
  })

  useEffect(() => {
    form.setFieldValue(
      "employees",
      role?.roleMembers?.map((x) => x?.nick_name)
    )
    setEmployees(role?.roleMembers?.map((x) => x?.nick_name) || [])
  }, [role])

  const [canAddMember, canRemove] = usePermissions({
    allowedPermissions: [
      "can_change_team_member_roles",
      "can_remove_team_members",
    ],
  })

  return (
    <CustomModal
      onCancel={() => onClose()}
      title={
        `Assign members to ${role?.description}`.replace(/(role)/gi, " ") +
        " role"
      }
      open
      footer={null}
    >
      <LoadingRegion
        spinning={isGetting}
        keepMounted
      >
        <>
          <Form
            form={form}
            className=""
            layout="vertical"
          >
            <div className="py-4">
              <AppPeoplePicker
                form={form}
                employees={employees}
                fieldName="employees"
                onUpdate={(values) => {
                  setEmployees(values)
                }}
                useCustomData
                filteredEmployees={admins.map((it) => ({
                  full_name: it?.text,
                }))}
                isDisabled
                label=""
                maxTagCount={5}
              />
            </div>
          </Form>

          <div className="flex w-full justify-end pt-4 gap-3 mt-8">
            <Button
              color="secondary"
              onClick={() => onClose()}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              submitting={isLoading}
              onClick={(e) => {
                e.preventDefault()
                if (canRemove && canAddMember) {
                  if (!employees.length) return

                  mutate({
                    role_id: role?.role_id?.toString() || "",
                    assignments: employees
                      ?.map((x: string) =>
                        admins.find((it) => it?.text === x)?.id?.toString()
                      )
                      .filter((id): id is string => !!id),
                  })
                } else {
                  toast.error(
                    "You don't have the required permission to perform this operation"
                  )
                }
              }}
            >
              Done
            </Button>
          </div>
        </>
      </LoadingRegion>
    </CustomModal>
  )
}
